/* Pure helpers for the Build feature (unit tested in logic.test.ts): event normalization for the
   job stream, approval decisions, the artifact runtime bridge (method -> route, permission names),
   CSV, kanban grouping, file-set edits, validation and formatting. */
import type { AnyJobEvent, ApprovalDecision, RiskTier } from '../../lib/api-types';
import type { BizField, BizRecord, BuildItem, ProjectFile, SiteDto } from './types';

type Loose = Record<string, unknown>;

/* ---------- job events ---------- */

const TIER: Record<string, RiskTier> = { read: 0, change: 1, consequential: 2, sensitive: 3 };
const OUTCOME: Record<string, ApprovalDecision> = { allow: 'approved', allow_task: 'approved', deny: 'denied', timeout: 'expired', cancelled: 'denied' };

const describeDetails = (d: unknown): string | undefined => {
  if (!d || typeof d !== 'object') return undefined;
  const x = d as Loose;
  const parts: string[] = [];
  if (x.target) parts.push(`${String(x.target)}${x.title ? ` "${String(x.title)}"` : x.slug ? ` /p/${String(x.slug)}` : ''}`);
  if (x.visibility || x.auth) parts.push(`visible to: ${String(x.visibility ?? x.auth)}`);
  if (x.version !== undefined) parts.push(`version: ${String(x.version)}`);
  const caps = x.capabilities as Loose | undefined;
  if (caps && typeof caps === 'object') {
    const c = Object.entries(caps).filter(([, v]) => v && v !== 'none' && !(Array.isArray(v) && !v.length)).map(([k, v]) => (Array.isArray(v) ? `${k}: ${v.join(', ')}` : k === 'storage' ? `storage: ${String(v)}` : k));
    if (c.length) parts.push(`can: ${c.join('; ')}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
};

/** Maps the API's `@cx/shared` event shapes onto the shapes the web core reducer folds
    (`approval` objects with numeric tiers, `toolCallId`, `label`, nested artifacts, `completed`). */
export function normalizeBuildEvent(raw: AnyJobEvent): AnyJobEvent {
  const ev = raw as unknown as { type: string; data: Loose } & Loose;
  const d = (ev.data ?? {}) as Loose;
  const out = (data: unknown) => ({ ...ev, data }) as unknown as AnyJobEvent;
  switch (ev.type) {
    case 'approval.requested': {
      if (d.approval) return raw;
      const tier = typeof d.tier === 'number' ? (d.tier as RiskTier) : TIER[String(d.tier)] ?? 2;
      const summary = String(d.summary ?? 'Approve this step');
      const verb = /^(Take .* offline|Unpublish)/.test(summary) ? 'Unpublish' : /^Delete/.test(summary) ? 'Delete' : /^Publish/.test(summary) ? 'Publish' : 'Approve';
      return out({ approval: { id: String(d.approvalId), tier, summary, reversibility: describeDetails(d.details), primaryLabel: verb } });
    }
    case 'approval.resolved':
      return out({ ...d, decision: OUTCOME[String(d.decision)] ?? d.decision });
    case 'artifact.created':
      if (d.artifact) return raw;
      return out({ artifact: { id: String(d.artifactId), kind: String(d.kind ?? 'other'), title: String(d.title ?? 'Build'), version: 1, content: d.content, mime: d.mime } });
    case 'step.started':
      return out({ ...d, label: d.label ?? d.title ?? d.stepId });
    case 'tool.call.started':
    case 'tool.call.args':
    case 'tool.call.result':
      return out({ ...d, toolCallId: d.toolCallId ?? d.callId, isError: d.isError ?? (d.error ? true : undefined), result: d.result ?? d.error });
    case 'usage.updated':
      return out({ ...d, credits: d.credits ?? d.costCredits, inputTokens: d.inputTokens ?? d.tokensIn, outputTokens: d.outputTokens ?? d.tokensOut });
    case 'state.snapshot':
      return out({ state: {} });
    case 'run.finished':
      return out({ ...d, status: d.status === 'cancelled' ? 'cancelled' : 'completed' });
    default:
      return raw;
  }
}

/** The API's approve route takes `allow` / `allow_task` / `deny`. */
export function toApiDecision(decision: ApprovalDecision, allowForTask?: boolean): 'allow' | 'allow_task' | 'deny' {
  if (decision === 'denied' || decision === 'expired') return 'deny';
  return allowForTask ? 'allow_task' : 'allow';
}

/* ---------- artifact runtime bridge (README section 2) ---------- */

export type RuntimeMethod = 'ask' | 'storage.get' | 'storage.list' | 'storage.set' | 'connectors.call' | 'download';
export const RUNTIME_METHODS: RuntimeMethod[] = ['ask', 'storage.get', 'storage.list', 'storage.set', 'connectors.call', 'download'];

export interface RuntimeCall { method: 'GET' | 'POST'; path: string; query?: Record<string, string>; body?: unknown }

/** The route the parent calls (as the viewer) for one `window.cx` request. */
export function runtimeRoute(artifactId: string, method: RuntimeMethod, params: Loose): RuntimeCall {
  const base = `/api/v1/build/runtime/${encodeURIComponent(artifactId)}`;
  const scope = params.scope === 'shared' ? 'shared' : 'viewer';
  switch (method) {
    case 'ask':
      return { method: 'POST', path: `${base}/ask`, body: { prompt: String(params.prompt ?? ''), ...(params.system ? { system: String(params.system) } : {}), ...(params.model ? { model: String(params.model) } : {}) } };
    case 'storage.get':
      return { method: 'GET', path: `${base}/storage`, query: { scope, key: String(params.key ?? '') } };
    case 'storage.list':
      return { method: 'GET', path: `${base}/storage`, query: { scope } };
    case 'storage.set':
      return { method: 'POST', path: `${base}/storage`, body: { scope, key: String(params.key ?? ''), value: params.value === null || params.value === undefined ? null : String(params.value) } };
    case 'connectors.call':
      return { method: 'POST', path: `${base}/connectors`, body: { connector: String(params.connector ?? ''), action: String(params.action ?? ''), args: (params.args && typeof params.args === 'object' ? params.args : {}) } };
    case 'download':
      return { method: 'POST', path: `${base}/download`, body: { name: String(params.name ?? 'download.txt'), mime: String(params.mime ?? 'text/plain'), content: String(params.content ?? ''), encoding: params.encoding === 'base64' ? 'base64' : 'utf8' } };
  }
}

/** The grant name a call needs when the server answers 403 permission_required (null: never prompted). */
export function grantFor(method: RuntimeMethod, params: Loose): string | null {
  if (method === 'ask') return 'ask';
  if (method.startsWith('storage.')) return params.scope === 'shared' ? 'storage.shared' : null;
  if (method === 'connectors.call') return `connector:${String(params.connector ?? '')}`;
  return null;
}

/** Plain-language prompt for a grant (permission dialog). */
export function grantPrompt(grant: string, title: string): { title: string; body: string } {
  if (grant === 'ask') return { title: 'Let this artifact ask the assistant?', body: `"${title}" wants to send prompts to the assistant. Replies use your own credits and sign-in, never the creator's.` };
  if (grant === 'storage.shared') return { title: 'Let this artifact use shared storage?', body: `"${title}" wants to save small text that every viewer of it can read. Do not store anything private there.` };
  if (grant.startsWith('connector:')) {
    const id = grant.slice('connector:'.length);
    return { title: `Let this artifact use your ${id} connection?`, body: `"${title}" wants to call ${id} with your own connected account. It only sees what that call returns.` };
  }
  return { title: 'Allow this?', body: `"${title}" asks for ${grant}.` };
}

/** True when a postMessage payload is a well-formed bridge request. */
export function isBridgeRequest(d: unknown): d is { type: 'cx:request'; v: 1; id: string; method: RuntimeMethod; params?: Loose } {
  if (!d || typeof d !== 'object') return false;
  const x = d as Loose;
  return x.type === 'cx:request' && x.v === 1 && typeof x.id === 'string' && x.id.length <= 64 && RUNTIME_METHODS.includes(x.method as RuntimeMethod);
}

/* ---------- files ---------- */

/** Applies a file edit (save, remove) to a file set, sorted by path. */
export function applyFileEdits(files: ProjectFile[], edits: { files?: ProjectFile[]; remove?: string[] }): ProjectFile[] {
  const map = new Map(files.map((f) => [f.path, f]));
  for (const p of edits.remove ?? []) map.delete(p);
  for (const f of edits.files ?? []) map.set(f.path, { encoding: 'utf8', ...f });
  return [...map.values()].sort((a, b) => a.path.localeCompare(b.path));
}

/** Simple line diff (LCS) for local "unsaved changes" views. */
export function lineDiff(a: string, b: string): { op: ' ' | '+' | '-'; text: string; a: number | null; b: number | null }[] {
  const x = a.split('\n'), y = b.split('\n');
  const n = x.length, m = y.length;
  if (n * m > 4_000_000) return [...x.map((t, i) => ({ op: '-' as const, text: t, a: i + 1, b: null })), ...y.map((t, j) => ({ op: '+' as const, text: t, a: null, b: j + 1 }))];
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i]![j] = x[i] === y[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
  const out: { op: ' ' | '+' | '-'; text: string; a: number | null; b: number | null }[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (x[i] === y[j]) { out.push({ op: ' ', text: x[i]!, a: i + 1, b: j + 1 }); i++; j++; }
    else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) { out.push({ op: '-', text: x[i]!, a: i + 1, b: null }); i++; }
    else { out.push({ op: '+', text: y[j]!, a: null, b: j + 1 }); j++; }
  }
  while (i < n) { out.push({ op: '-', text: x[i]!, a: i + 1, b: null }); i++; }
  while (j < m) { out.push({ op: '+', text: y[j]!, a: null, b: j + 1 }); j++; }
  return out;
}

/* ---------- CSV ---------- */

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (q) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') q = false;
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

const csvCell = (v: unknown): string => {
  let s = v === null || v === undefined ? '' : Array.isArray(v) ? v.join('; ') : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`; // formula-injection guard, same rule as the API export
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCsv(fields: Pick<BizField, 'id' | 'name'>[], records: Pick<BizRecord, 'values'>[]): string {
  const head = fields.map((f) => csvCell(f.name)).join(',');
  const body = records.map((r) => fields.map((f) => csvCell(r.values[f.id])).join(','));
  return [head, ...body].join('\r\n') + '\r\n';
}

/* ---------- business views ---------- */

export function kanbanColumns(groupBy: BizField, records: BizRecord[]): { value: string | null; records: BizRecord[] }[] {
  return [...(groupBy.options ?? []), null].map((opt) => ({ value: opt, records: records.filter((r) => ((r.values[groupBy.id] as string | null | undefined) ?? null) === opt) }));
}

export function displayValue(f: Pick<BizField, 'type'>, v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (f.type === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  if (f.type === 'date') { const s = String(v); return s.length > 10 && s.includes('T') ? s.slice(0, 10) : s; }
  return String(v);
}

/** Converts a form input string to the field's value type (the API coerces again). */
export function inputValue(f: Pick<BizField, 'type'>, raw: string | boolean): unknown {
  if (typeof raw === 'boolean') return raw;
  if (raw === '') return null;
  if (f.type === 'number') { const n = Number(raw); return Number.isFinite(n) ? n : raw; }
  if (f.type === 'multi_select') return raw.split(',').map((s) => s.trim()).filter(Boolean);
  return raw;
}

/* ---------- validation ---------- */

export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
export const SECRET_RE = /^[A-Z][A-Z0-9_]{0,63}$/;
export const TABLE_RE = /^[a-z][a-z0-9_]{0,40}$/;
export const DOMAIN_RE = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
export const PACKAGE_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

export const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'app';
export const validSlug = (s: string) => s.length >= 3 && s.length <= 40 && SLUG_RE.test(s);
export const validSecretName = (s: string) => SECRET_RE.test(s);
export const validDomain = (s: string) => DOMAIN_RE.test(s.trim().toLowerCase());

/* ---------- display ---------- */

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

export function formatGb(gb: number): string {
  if (gb === 0) return '0 GB';
  if (gb < 0.001) return `${Math.max(1, Math.round(gb * 1e6))} KB`;
  if (gb < 1) return `${Math.round(gb * 1000)} MB`;
  return `${gb.toFixed(1)} GB`;
}

export function siteStatus(site: SiteDto | null | undefined): { label: string; tone: 'ok' | 'warn' | 'neutral' } {
  if (!site) return { label: 'Not hosted', tone: 'neutral' };
  if (site.status === 'published') return { label: site.auth === 'public' ? 'Live · public' : 'Live · team only', tone: 'ok' };
  if (site.status === 'unpublished') return { label: 'Offline', tone: 'warn' };
  return { label: 'Draft', tone: 'neutral' };
}

export function dnsStatus(site: SiteDto | null | undefined): { label: string; tone: 'ok' | 'warn' | 'neutral' } {
  const d = site?.customDomain;
  if (!d) return { label: 'No custom domain', tone: 'neutral' };
  return d.status === 'verified' ? { label: 'DNS verified', tone: 'ok' } : { label: 'Waiting for DNS', tone: 'warn' };
}

/** Absolute URL for a link the user copies or opens (API paths are relative locally). */
export function absoluteUrl(url: string): string {
  if (/^https?:\/\//.test(url)) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

export const ago = (iso: string | null | undefined, now = Date.now()): string => {
  if (!iso) return '';
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

/** Google Play submission steps (BUILD-03: the store release stays with the user). */
export const PLAY_STEPS = [
  { id: 'account', title: 'Open your Google Play Console', body: 'Use your own developer account (one-time $25 fee to Google). Crosspoint never signs in for you.' },
  { id: 'app', title: 'Create the app', body: 'Pick the app name and default language, and answer the app or game and free or paid questions.' },
  { id: 'listing', title: 'Fill in the store listing', body: 'Short and full description, icon, screenshots, privacy policy URL and the content rating questionnaire.' },
  { id: 'upload', title: 'Upload the build to internal testing', body: 'Testing › Internal testing › Create release, then upload the signed bundle you built in Android Studio from the source zip.' },
  { id: 'review', title: 'Send for review', body: 'Add testers, roll out, then promote to production when you are ready. Google reviews every release.' },
] as const;

/** Sort the left list: web, artifacts, mobile, games, systems, designs. */
const ORDER: Record<BuildItem['tab'], number> = { web: 0, artifact: 1, mobile: 2, game: 3, biz: 4, design: 5 };
export const sortItems = (items: BuildItem[]) => [...items].sort((a, b) => ORDER[a.tab] - ORDER[b.tab]);
